import React, { useState, useCallback, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  ReactFlowProvider,
  Panel
} from "reactflow";
import "reactflow/dist/style.css";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import NodeSearch from "./SearchMindmap";

//
// Light dynamic color generator
//
function getLightColor(seed, depth = 0) {
  const hue = Math.abs([...seed].reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
  const lightness = 90;
  return `hsl(${hue}, 70%, ${Math.max(60, lightness)}%)`;
}

//
// Unified Node Component
//
const MindNode = ({ data }) => {
  const { title, onToggle, hasChildren, expanded, color, varitant } = data;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 250, damping: 20 }}
      whileHover={{ scale: 1.05 }}
      onClick={onToggle}
      style={{
        background: color,
        border: "1px solid rgba(0,0,0,0.15)",
        borderRadius: "10px",
        padding: "10px 16px",
        cursor: hasChildren ? "pointer" : "default",
        textAlign: "center",
        boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
        minWidth: 150,
        transition: "all 0.3s ease",
      }}
    >
      <div
        className="mind-node-title"
        style={{ fontWeight: 500, fontSize: "0.95em", color: "#222" }}
      >
        {title}
      </div>

      {hasChildren && (
        <div
          style={{
            fontSize: "0.8em",
            color: "#444",
            marginTop: 4,
          }}
        >
          {expanded ? "▼ Collapse" : "▶ Expand"}
        </div>
      )}

      {varitant !== "output" && <Handle type="source" position="right" id="a" />}
      {varitant !== "input" && <Handle type="target" position="left" id="b" />}
    </motion.div>
  );
};

const nodeTypes = { mindNode: MindNode };

//
// Layout Algorithm
//
function layoutTree(baseNodes, expanded, startX = 290, startY = 100) {
  const positionMap = new Map();
  const nodeSpacingY = 90;
  let nextY = startY;

  const calculateLevelSpacing = (nodeId) => {
    const node = baseNodes.find((n) => n.id === nodeId);
    if (!node) return 200;
    const titleLength = node.title.length;
    return 200 + titleLength * 8;
  };

  const layoutNode = (nodeId, depth = 0, parentX = startX) => {
    const node = baseNodes.find((n) => n.id === nodeId);
    if (!node) return nextY;

    const children = baseNodes.filter((n) => n.parent === nodeId);

    if (!children.length || !expanded[nodeId]) {
      positionMap.set(nodeId, { x: parentX, y: nextY });
      nextY += nodeSpacingY;
      return positionMap.get(nodeId).y;
    }

    const levelSpacingX = calculateLevelSpacing(nodeId);
    const childYs = children.map((child) =>
      layoutNode(child.id, depth + 1, parentX + levelSpacingX)
    );

    const minY = Math.min(...childYs);
    const maxY = Math.max(...childYs);
    const myY = (minY + maxY) / 2;

    positionMap.set(nodeId, { x: parentX, y: myY });
    return myY;
  };

  const roots = baseNodes.filter((n) => !n.parent);
  roots.forEach((root) => layoutNode(root.id));

  return positionMap;
}

//
// Main Component
//
export default function MindMapViewer({ AISelectedApp, caseName }) {
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  function transformMindMapData(apiData, casename) {
    if (!Array.isArray(apiData)) return [{ id: "0", title: casename }];

    const transformed = apiData.map((item) => ({
      id: item.mindMapSeriesNo,
      title: item.mindMapTitle,
      parent: item.mindMapParentSeriesNo || "0",
    }));

    return [{ id: "0", title: `File Name: ${casename}` }, ...transformed];
  }

  
  const baseNodes = useMemo(() => {
    const rawData = AISelectedApp?.appData;
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [{ id: "0", title: "No data available" }];
    }

    return transformMindMapData(rawData, caseName);
  }, [AISelectedApp, caseName]);

  const [expanded, setExpanded] = useState({ "0": true });

  //
  // Toggle + Auto-Center
  //
  const toggleNode = useCallback(
    (id) => {
      setExpanded((prev) => {
        const isExpanding = !prev[id];
        const newState = { ...prev, [id]: isExpanding };

        if (!isExpanding) {
          // Collapse descendants
          const collapseChildren = (parentId) => {
            baseNodes.forEach((node) => {
              if (node.parent === parentId) {
                newState[node.id] = false;
                collapseChildren(node.id);
              }
            });
          };
          const allNodes = reactFlowInstance.getNodes();
          const thisNode = allNodes.find((n) => n.parent === id);
          console.log(thisNode)
          if (thisNode) {
            reactFlowInstance.setCenter(
              thisNode.position.x - 150,
              thisNode.position.y,
              { zoom: 1.5, duration: 600 }
            );
          }

          collapseChildren(id);
        } else if (reactFlowInstance) {
          setTimeout(() => {
            const allNodes = reactFlowInstance.getNodes();
            const childNodes = allNodes.filter((n) => n.parent === id);

            if (childNodes.length > 0) {
              reactFlowInstance.fitView({
                nodes: childNodes,
                padding: 0.3,
                duration: 900,
                minZoom: 0.8,
                maxZoom: 1.6,
              });
            } else {
              // fallback to centering the node itself
              const thisNode = allNodes.find((n) => n.id === id);
              if (thisNode) {
                reactFlowInstance.setCenter(
                  thisNode.position.x,
                  thisNode.position.y,
                  { zoom: 1.5, duration: 200 }
                );
              }
            }
          }, 300);
        }

        return newState;
      });
    },
    [baseNodes, reactFlowInstance]
  );

  //
  // Position Nodes
  //
  const positionedNodes = useMemo(() => {
    const positionMap = layoutTree(baseNodes, expanded);

    const getDepth = (node) => {
      let depth = 0;
      let parent = node.parent;
      while (parent) {
        parent = baseNodes.find((x) => x.id === parent)?.parent;
        depth++;
      }
      return depth;
    };

    return baseNodes.map((n) => {
      const hasChildren = baseNodes.some((x) => x.parent === n.id);
      const color = getLightColor(n.title, getDepth(n));
      const isRoot = !n.parent;
      const isLeaf = !hasChildren;
      const nodeType = isRoot ? "input" : isLeaf ? "output" : "mindNode";

      return {
        id: n.id,
        type: "mindNode",
        position: positionMap.get(n.id) ?? { x: 250, y: 100 },
        data: {
          title: n.title,
          color,
          hasChildren,
          expanded: expanded[n.id],
          onToggle: () => toggleNode(n.id),
          varitant: nodeType,
        },
        parent: n.parent,
        sourcePosition: "right",
        targetPosition: "left",
      };
    });
  }, [baseNodes, expanded, toggleNode]);

  //
  // Visible Nodes & Edges
  //
  const visibleNodes = useMemo(() => {
    return positionedNodes.filter((n) => {
      let parent = n.parent;
      while (parent) {
        if (!expanded[parent]) return false;
        parent = baseNodes.find((x) => x.id === parent)?.parent;
      }
      return true;
    });
  }, [positionedNodes, expanded, baseNodes]);

  const visibleEdges = useMemo(
    () =>
      baseNodes
        .filter((n) => n.parent)
        .map((n) => ({
          id: `e-${n.parent}-${n.id}`,
          source: n.parent,
          target: n.id,
          animated: true,
        }))
        .filter(
          (e) =>
            visibleNodes.find((n) => n.id === e.source) &&
            visibleNodes.find((n) => n.id === e.target)
        ),
    [baseNodes, visibleNodes]
  );

  //
  //  Render
  //
  return (
    <>
      <div className="container mt-2">
        <Tooltip id="ai-summary-tooltip" className="tooltip-custom align-items-center" />
        <h3
          className="d-flex justify-content-between align-items-center w-100"
          data-tooltip-id="ai-summary-tooltip"
          data-tooltip-content={AISelectedApp?.appName || "AttestaAI MindMap"}
        >
          <div className="analysis-row">
            <div className="d-flex align-items-center">
              <div className="custom-icon-wrapper">
                <span
                  className="custom-icon attesta-blue"
                  dangerouslySetInnerHTML={{
                    __html:
                      AISelectedApp?.appIcon ||
                      '<i class="fa-solid fa-brain custom-icon"></i>',
                  }}
                ></span>
              </div>
              <span className="ms-2 attesta-blue">
                {AISelectedApp?.appName || "AttestaAI MindMap"}
              </span>
            </div>
          </div>
        </h3>
      </div>

      <div style={{ height: "100vh", width: "100%" }}>
        <ReactFlowProvider>
          <ReactFlow
            onInit={setReactFlowInstance}
            nodes={visibleNodes}
            edges={visibleEdges}
            nodeTypes={nodeTypes}
            fitView
            nodesDraggable={false}
            nodesConnectable={false}
            zoomOnScroll
            panOnDrag
            style={{ color: "rgba(255, 255, 255, 1)" }}
          >
            <Panel position="top-left">
              <NodeSearch nodes={visibleNodes} />
            </Panel>

            <Background gap={12} color="#ffffffff" />
            <MiniMap />
            <Controls />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </>
  );
}
